import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { KeyOutlined, ArrowBack } from '@mui/icons-material';

import ForgotPasswordForm from '../forms/ForgotPasswordForm';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

function ForgotPassword() {
  const config = useConfig();

  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate, config.company.name]);

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20,
      },
    },
  };

  return (
    <>
      <motion.div
        className="min-h-[75vh] w-full flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="w-full max-w-md" variants={itemVariants}>
          <div className="text-center mb-8">
            <motion.div
              className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
                delay: 0.3,
              }}
            >
              <KeyOutlined className="h-8 w-8 text-primary" />
            </motion.div>

            <motion.h1
              className="text-3xl font-bold text-gray-900"
              variants={itemVariants}
            >
              {submitted ? 'Email wysłany' : 'Odzyskiwanie hasła'}
            </motion.h1>

            <motion.p className="mt-2 text-gray-600" variants={itemVariants}>
              {submitted
                ? 'Sprawdź swoją skrzynkę odbiorczą'
                : 'Wyślemy Ci link do zresetowania hasła'}
            </motion.p>
          </div>

          <motion.div
            className="bg-white py-8 px-6 shadow-xl sm:rounded-lg sm:px-10 border border-gray-100"
            variants={itemVariants}
            whileHover={{
              boxShadow:
                '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
            }}
            transition={{ duration: 0.3 }}
          >
            {submitted ? (
              <motion.div
                className="text-center space-y-6"
                variants={successVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="rounded-full h-20 w-20 bg-green-100 mx-auto flex items-center justify-center">
                  <motion.svg
                    className="h-10 w-10 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </motion.svg>
                </div>

                <div className="space-y-4">
                  <p className="text-gray-700">
                    Link do zresetowania hasła został wysłany na podany adres
                    email.
                  </p>

                  <p className="text-sm text-gray-500">
                    Sprawdź swoją skrzynkę odbiorczą oraz folder spam. Link
                    wygasa po 30 minutach.
                  </p>
                </div>

                <motion.div
                  className="pt-4"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Link
                    to="/logowanie"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                  >
                    <ArrowBack fontSize="small" />
                    Wróć do logowania
                  </Link>
                </motion.div>
              </motion.div>
            ) : (
              <div className="mb-6">
                <ForgotPasswordForm setSubmitted={setSubmitted} />
              </div>
            )}
          </motion.div>

          {!submitted && (
            <motion.div
              className="mt-6 flex items-center justify-center"
              variants={itemVariants}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/logowanie"
                  className="flex items-center text-primary hover:text-primary-dark transition-colors"
                >
                  <ArrowBack className="mr-1" fontSize="small" />
                  <span>Wróć do logowania</span>
                </Link>
              </motion.div>
            </motion.div>
          )}

          <motion.p
            className="mt-6 text-center text-sm text-gray-500"
            variants={itemVariants}
          >
            Potrzebujesz pomocy?
            <motion.a
              href="/kontakt"
              className="ml-1 font-medium text-primary hover:text-primary-dark"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Skontaktuj się z nami
            </motion.a>
          </motion.p>
        </motion.div>
      </motion.div>
      <Seo
        title={`Odzyskiwanie hasła`}
        description="Odzyskaj swoje hasło do konta. Wprowadź swój adres e-mail, a my wyślemy Ci link do zresetowania hasła."
        image={config.company.logo}
        noindex={true}
      ></Seo>
    </>
  );
}

export default ForgotPassword;
